import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, Camera, Clock, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Exam() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user, formatApiError } = useAuth();

  const [stage, setStage] = useState("pre"); // pre | selfie | exam | submitted
  const [test, setTest] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [selfieData, setSelfieData] = useState(null);
  const [showViolation, setShowViolation] = useState(false);
  const [violationMsg, setViolationMsg] = useState("");
  const [violationCount, setViolationCount] = useState(0);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const submitRef = useRef(null);
  const recorderLoopRef = useRef({ active: false });

  // Proctoring: high-frequency image snapshots (video-like motion) +
  // separate periodic audio clips. This dual-stream approach avoids the
  // MediaRecorder webm duration bug entirely — JPEG frames always render
  // and audio clips have rock-solid duration metadata.
  useEffect(() => {
    if (stage !== "exam" || !attemptId) return;

    const ctrl = { active: true };
    recorderLoopRef.current = ctrl;
    let frameErrorCount = 0;

    // --- Frame capture (every 800ms → feels like slow video) ---
    const captureFrame = async () => {
      try {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || !streamRef.current) return;
        if (!video.videoWidth || !video.videoHeight || video.readyState < 2) return;
        canvas.width = 640;
        canvas.height = 480;
        canvas.getContext("2d").drawImage(video, 0, 0, 640, 480);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
        if (!blob) return;
        const fd = new FormData();
        fd.append("file", new File([blob], "frame.jpg", { type: "image/jpeg" }));
        await api.post(`/exam/${attemptId}/snapshot`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        frameErrorCount = 0;
      } catch (err) {
        frameErrorCount += 1;
        // eslint-disable-next-line no-console
        console.warn("[proctor] frame upload failed:", err?.message || err);
        if (frameErrorCount === 1) toast.error("Live feed upload failed — check connection");
      }
    };

    // --- Audio clip recorder (5s each, audio-only = simple valid format) ---
    const pickAudioMime = () => {
      const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
      return cands.find((m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) || "";
    };
    const audioMime = pickAudioMime();

    const recordAudioClip = () =>
      new Promise((resolve) => {
        try {
          if (!streamRef.current) return resolve();
          const audioOnly = new MediaStream(streamRef.current.getAudioTracks());
          if (audioOnly.getTracks().length === 0) return resolve();
          const chunks = [];
          const mr = new MediaRecorder(audioOnly, audioMime ? { mimeType: audioMime } : undefined);
          mr.ondataavailable = (e) => e.data && e.data.size > 0 && chunks.push(e.data);
          mr.onerror = () => resolve();
          mr.onstop = async () => {
            try {
              const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
              if (blob.size < 200) return resolve();
              const fd = new FormData();
              const ext = (blob.type || "").includes("mp4") ? "m4a" : (blob.type || "").includes("ogg") ? "ogg" : "webm";
              fd.append("file", new File([blob], `audio.${ext}`, { type: blob.type || "audio/webm" }));
              await api.post(`/exam/${attemptId}/audio-clip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn("[proctor] audio upload failed:", err?.message || err);
            }
            resolve();
          };
          mr.start();
          setTimeout(() => { try { if (mr.state === "recording") mr.stop(); else resolve(); } catch { resolve(); } }, 5000);
        } catch {
          resolve();
        }
      });

    // eslint-disable-next-line no-console
    console.log("[proctor] Started dual-stream proctor. audioMime=", audioMime || "default");

    // Frame loop
    const frameTimer = setInterval(() => { if (ctrl.active) captureFrame(); }, 800);
    // first frame immediately
    setTimeout(captureFrame, 500);
    // Audio loop
    (async () => {
      await new Promise((r) => setTimeout(r, 600));
      while (ctrl.active) {
        await recordAudioClip();
        await new Promise((r) => setTimeout(r, 100));
      }
    })();

    return () => {
      ctrl.active = false;
      clearInterval(frameTimer);
    };
  }, [stage, attemptId]);

  // Re-attach active stream to the current <video> element whenever the
  // stage changes (selfie unmounts its video, exam mounts a new one).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !streamRef.current) return;
    if (v.srcObject !== streamRef.current) {
      v.srcObject = streamRef.current;
      v.play?.().catch(() => {});
    }
  }, [stage]);

  // Prevent right-click & copy during exam
  useEffect(() => {
    if (stage !== "exam") return;
    const prevent = (e) => { e.preventDefault(); return false; };
    const onCopy = () => toast.error("Copy is disabled during the exam.");
    document.addEventListener("contextmenu", prevent);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCopy);
    return () => {
      document.removeEventListener("contextmenu", prevent);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCopy);
    };
  }, [stage]);

  // Tab / visibility / blur detection
  useEffect(() => {
    if (stage !== "exam" || !attemptId) return;

    const reportViolation = async (type, details) => {
      setViolationCount((c) => c + 1);
      setViolationMsg(details);
      setShowViolation(true);
      try {
        await api.post(`/exam/${attemptId}/violation`, { type, details });
      } catch {}
    };

    const onVisibility = () => {
      if (document.hidden) {
        reportViolation("tab_switch", "You switched tabs or minimized the window.");
      }
    };
    const onBlur = () => {
      reportViolation("window_blur", "Exam window lost focus.");
    };
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        reportViolation("fullscreen_exit", "You exited fullscreen mode.");
        // attempt to re-enter
        setTimeout(() => {
          try { document.documentElement.requestFullscreen?.(); } catch {}
        }, 500);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [stage, attemptId]);

  // Timer
  useEffect(() => {
    if (stage !== "exam") return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          if (submitRef.current) submitRef.current();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [stage]);

  // Auto-submit at 3 violations (industry-standard threshold for proctored exams)
  const VIOLATION_LIMIT = 3;
  useEffect(() => {
    if (violationCount > 0 && violationCount < VIOLATION_LIMIT && stage === "exam") {
      const remaining = VIOLATION_LIMIT - violationCount;
      toast.warning(`Warning ${violationCount}/${VIOLATION_LIMIT} — ${remaining} more violation${remaining !== 1 ? "s" : ""} will auto-submit your exam.`, { duration: 5000 });
    }
    if (violationCount >= VIOLATION_LIMIT && stage === "exam") {
      toast.error("Exam auto-submitted due to repeated violations.");
      submitExam();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [violationCount]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true; // don't play own audio back
        await videoRef.current.play();
      }
      setStage("selfie");
    } catch (e) {
      toast.error("Camera and microphone access is required to start the exam.");
    }
  };

  const captureSelfie = async () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    canvas.width = 320;
    canvas.height = 240;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, 320, 240);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    setSelfieData(dataUrl);
    // upload selfie
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append("file", new File([blob], "selfie.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } catch {
      return "";
    }
  };

  const beginExam = async () => {
    let selfieUrl = "";
    if (!selfieData) {
      selfieUrl = (await captureSelfie()) || "";
    }
    try {
      const { data } = await api.post(`/exam/start/${testId}${selfieUrl ? `?selfie_url=${encodeURIComponent(selfieUrl)}` : ""}`);
      setTest(data);
      setAttemptId(data.attempt_id);
      setQuestions(data.questions);
      setTimeLeft(data.duration_minutes * 60);
      // enter fullscreen
      try { await document.documentElement.requestFullscreen(); } catch {}
      setStage("exam");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitExam = useCallback(async () => {
    if (!attemptId) return;
    if (recorderLoopRef.current) recorderLoopRef.current.active = false;
    const payload = Object.entries(answers).map(([qi, opt]) => ({ question_index: Number(qi), selected_option: opt }));
    try {
      await api.post(`/exam/${attemptId}/submit`, payload);
      setStage("submitted");
      try { document.exitFullscreen?.(); } catch {}
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [attemptId, answers, formatApiError]);

  submitRef.current = submitExam;

  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");

  // ---------- Pre stage ----------
  if (stage === "pre") {
    return (
      <div className="min-h-screen exam-bg text-white flex items-center justify-center p-6">
        <div className="max-w-xl w-full p-8 rounded-2xl exam-card">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400">Secure Exam</div>
          <h1 className="heading text-3xl font-bold mt-3">Before you start</h1>
          <ul className="mt-5 space-y-2 text-sm text-slate-300">
            <li>• You must enable your <b className="text-white">camera</b> for selfie verification.</li>
            <li>• The exam runs in <b className="text-white">fullscreen</b>. Exiting is a violation.</li>
            <li>• Switching tabs, minimizing, or losing window focus will be logged.</li>
            <li>• Right-click and copy are disabled during the exam.</li>
            <li>• Too many violations will <b className="text-white">auto-submit</b> your exam.</li>
            <li>• Result will be announced by <b className="text-white">Admin</b>.</li>
          </ul>
          <button
            onClick={startCamera}
            className="mt-7 w-full bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg py-3.5 flex items-center justify-center gap-2"
            data-testid="exam-enable-camera-button"
          >
            <Camera size={18} /> Enable Camera & Continue
          </button>
          <button onClick={() => navigate("/student/test-series")} className="mt-3 w-full text-slate-400 text-sm hover:text-white" data-testid="exam-cancel-button">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ---------- Selfie stage ----------
  if (stage === "selfie") {
    return (
      <div className="min-h-screen exam-bg text-white flex items-center justify-center p-6">
        <div className="max-w-xl w-full p-8 rounded-2xl exam-card text-center">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400">Selfie Verification</div>
          <h1 className="heading text-2xl font-bold mt-3">Center your face in the frame</h1>
          <div className="mt-5 rounded-xl overflow-hidden border-4 border-amber-500 mx-auto w-full max-w-sm aspect-[4/3] bg-black">
            <video ref={videoRef} className="w-full h-full object-cover" autoPlay muted playsInline />
          </div>
          <canvas ref={canvasRef} className="hidden" />
          <button
            onClick={beginExam}
            className="mt-6 w-full bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg py-3.5 flex items-center justify-center gap-2"
            data-testid="exam-start-button"
          >
            Capture & Start Exam →
          </button>
        </div>
      </div>
    );
  }

  // ---------- Submitted ----------
  if (stage === "submitted") {
    return (
      <div className="min-h-screen exam-bg text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full p-10 rounded-2xl exam-card text-center">
          <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
          <h1 className="heading text-3xl font-bold mt-4">Submitted Successfully</h1>
          <p className="mt-3 text-slate-300">
            Your exam has been submitted securely. <b className="text-amber-400">Result will be announced by Admin.</b>
          </p>
          <button
            onClick={() => navigate("/student/results")}
            className="mt-6 bg-white text-slate-900 font-bold rounded-lg px-6 py-3"
            data-testid="exam-view-results-button"
          >
            Go to My Results
          </button>
        </div>
      </div>
    );
  }

  // ---------- Exam stage ----------
  const current = questions[currentIdx];
  const answered = Object.keys(answers).length;

  return (
    <div className="min-h-screen exam-bg text-white no-select" data-testid="exam-runner">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur-md border-b border-slate-700 px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">PMR Secure Exam</div>
          <div className="text-sm font-semibold">{user?.name}</div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">Time Left</div>
            <div className={`font-mono text-2xl font-black ${timeLeft < 60 ? "text-red-400" : "text-amber-400"} flex items-center gap-1`}>
              <Clock size={18} /> {mm}:{ss}
            </div>
          </div>
          {/* Selfie preview in corner — hidden video keeps the stream alive for proctoring */}
          <div className="hidden md:block w-14 h-14 rounded-full overflow-hidden border-2 border-amber-500 relative">
            <video ref={videoRef} className="w-full h-full object-cover" autoPlay muted playsInline />
            <span className="absolute bottom-0 right-0 bg-red-600 text-white text-[8px] font-black px-1 rounded-tl">● REC</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr_280px] gap-5">
        {/* Question */}
        <div className="exam-card rounded-2xl p-6 md:p-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-400 font-bold">Question {currentIdx + 1} of {questions.length}</div>
          <h2 className="heading text-xl md:text-2xl font-bold mt-3">{current?.question}</h2>
          <div className="mt-6 space-y-3">
            {current?.options.map((opt, i) => {
              const selected = answers[currentIdx] === i;
              return (
                <button
                  key={i}
                  onClick={() => setAnswers({ ...answers, [currentIdx]: i })}
                  className={`w-full text-left p-4 rounded-xl border-2 transition ${
                    selected ? "border-amber-500 bg-amber-500/10" : "border-slate-700 hover:border-slate-500 bg-slate-800/50"
                  }`}
                  data-testid={`exam-option-${i}`}
                >
                  <span className="font-mono text-amber-400 font-bold mr-2">{String.fromCharCode(65 + i)}.</span>
                  {opt}
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
              disabled={currentIdx === 0}
              className="px-5 py-2.5 rounded-lg border border-slate-600 font-semibold disabled:opacity-40"
              data-testid="exam-prev-button"
            >
              ← Previous
            </button>
            {currentIdx < questions.length - 1 ? (
              <button
                onClick={() => setCurrentIdx((i) => Math.min(questions.length - 1, i + 1))}
                className="px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 font-bold"
                data-testid="exam-next-button"
              >
                Next →
              </button>
            ) : (
              <button
                onClick={submitExam}
                className="px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 font-bold flex items-center gap-2"
                data-testid="exam-submit-button"
              >
                <Send size={16} /> Submit Exam
              </button>
            )}
          </div>
        </div>

        {/* Question palette */}
        <aside className="exam-card rounded-2xl p-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-bold">Palette</div>
          <div className="text-sm mt-2 text-slate-300">Answered: <b className="text-emerald-400">{answered}</b> / {questions.length}</div>
          <div className="mt-4 grid grid-cols-5 gap-2">
            {questions.map((_, i) => {
              const ans = answers[i] !== undefined;
              const cur = i === currentIdx;
              return (
                <button
                  key={i}
                  onClick={() => setCurrentIdx(i)}
                  className={`aspect-square rounded-md text-sm font-bold font-mono border ${
                    cur ? "bg-amber-500 text-slate-900 border-amber-500" :
                    ans ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/50" :
                    "bg-slate-800 text-slate-400 border-slate-700"
                  }`}
                  data-testid={`exam-palette-${i}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
          <button
            onClick={submitExam}
            className="mt-5 w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 font-bold"
            data-testid="exam-palette-submit-button"
          >
            Submit Exam
          </button>
          {violationCount > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs">
              ⚠ Violations logged: <b>{violationCount}</b>
            </div>
          )}
        </aside>
      </div>

      <canvas ref={canvasRef} className="hidden" />
      {/* Violation overlay */}
      {showViolation && (
        <div className="fixed inset-0 z-50 bg-red-900/90 backdrop-blur-sm flex items-center justify-center p-6 animate-shake">
          <div className="max-w-md p-8 rounded-2xl bg-red-950 border-2 border-red-500 text-white text-center">
            <AlertTriangle size={48} className="mx-auto text-red-400" />
            <h2 className="heading text-2xl font-bold mt-4">VIOLATION DETECTED</h2>
            <p className="mt-2 text-red-200">{violationMsg}</p>
            <p className="mt-4 text-sm text-red-300">Violations logged: <b>{violationCount}</b> / 8 (auto-submit)</p>
            <button
              onClick={() => setShowViolation(false)}
              className="mt-6 px-6 py-2.5 bg-white text-red-900 font-bold rounded-lg"
              data-testid="violation-acknowledge-button"
            >
              I Understand, Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
